// src/pages/public/CreditApplicationForm.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Building2,
  FileText,
  Mail,
  Phone,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Save,
  Clock,
  Eye,
  CheckSquare,
  Lock,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { CreditSignaturePad } from '../../components/credit/CreditSignaturePad';
import { CreditEmailVerification } from '../../components/credit/CreditEmailVerification';
import { CreditLocationConsent } from '../../components/credit/CreditLocationConsent';
import { CreditDocumentPreview } from '../../components/credit/CreditDocumentPreview';

export const CreditApplicationForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [app, setApp] = useState<any>(null);

  // Form State
  const [step, setStep] = useState(1);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error'>('saved');

  // Answers State
  const [businessType, setBusinessType] = useState('');
  const [establishedSince, setEstablishedSince] = useState('');
  const [emails, setEmails] = useState<string[]>(['', '', '']);
  const [phones, setPhones] = useState<string[]>(['', '', '']);
  const [descriptionMarket, setDescriptionMarket] = useState('');
  const [relationshipDuration, setRelationshipDuration] = useState('New Customer');
  const [creditLimit, setCreditLimit] = useState<number | ''>('');
  const [creditTerms, setCreditTerms] = useState('30 Days');
  const [customTerms, setCustomTerms] = useState('');
  const [creditType, setCreditType] = useState('Open Credit Account');
  const [creditTypeOther, setCreditTypeOther] = useState('');
  const [purchaseVolume, setPurchaseVolume] = useState('');
  const [businessJustification, setBusinessJustification] = useState('');

  // Signatory State
  const [signatoryName, setSignatoryName] = useState('');
  const [signatoryDesignation, setSignatoryDesignation] = useState('');
  const [signatoryCompany, setSignatoryCompany] = useState('');
  const [signatoryEmail, setSignatoryEmail] = useState('');
  const [signatoryPhone, setSignatoryPhone] = useState('');
  const [signatureMethod, setSignatureMethod] = useState<'draw' | 'type' | 'upload'>('upload');
  const [signatureData, setSignatureData] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);

  // Declarations
  const [declarationAccurate, setDeclarationAccurate] = useState(false);
  const [declarationAuthorized, setDeclarationAuthorized] = useState(false);
  const [declarationPrivacy, setDeclarationPrivacy] = useState(false);

  // Telemetry & Location
  const [gpsOutcome, setGpsOutcome] = useState<'granted' | 'denied' | 'not_requested'>('not_requested');
  const [gpsCoords, setGpsCoords] = useState<{ latitude: number; longitude: number; accuracy: number } | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  // Fetch initial application data
  useEffect(() => {
    if (!token) return;
    const fetchApp = async () => {
      try {
        setLoading(true);
        const res = await fetch(`/api/public/credit-application/${token}`);
        const data = await res.json<any>();
        if (!res.ok || !data.success) {
          throw new Error(data.error?.message || 'Failed to load credit application.');
        }

        const appData = data.data;
        setApp(appData);

        // Populate saved answers
        if (appData.answers) {
          const a = appData.answers;
          setBusinessType(a.business_type || '');
          setEstablishedSince(a.established_since || '');
          if (Array.isArray(a.customer_emails) && a.customer_emails.length > 0) {
            setEmails(a.customer_emails);
          }
          if (Array.isArray(a.customer_phones) && a.customer_phones.length > 0) {
            setPhones(a.customer_phones);
          }
          setDescriptionMarket(a.description_and_market_position || '');
          setRelationshipDuration(a.relationship_duration || 'New Customer');
          setCreditLimit(a.requested_credit_limit || '');
          setCreditTerms(a.requested_credit_terms || '30 Days');
          setCustomTerms(a.custom_credit_terms || '');
          setCreditType(a.credit_type || 'Open Credit Account');
          setCreditTypeOther(a.credit_type_other || '');
          setPurchaseVolume(a.expected_purchase_volume || '');
          setBusinessJustification(a.business_justification || '');
          if (a.current_step && a.current_step >= 1 && a.current_step <= 3) {
            setStep(a.current_step);
          }
        }

        // Initialize signatory company default
        setSignatoryCompany(appData.company_name);
        if (appData.primary_email && !signatoryEmail) setSignatoryEmail(appData.primary_email);
        if (appData.primary_phone && !signatoryPhone) setSignatoryPhone(appData.primary_phone);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchApp();
  }, [token]);

  // Debounced Autosave
  const autosaveTimerRef = useRef<any>(null);
  const performSave = async () => {
    if (!token || !app || app.status === 'submitted') return;
    try {
      setSaveStatus('saving');
      const payload = {
        business_type: businessType,
        established_since: establishedSince,
        customer_emails: emails,
        customer_phones: phones,
        description_and_market_position: descriptionMarket,
        relationship_duration: relationshipDuration,
        requested_credit_limit: creditLimit,
        requested_credit_terms: creditTerms,
        custom_credit_terms: customTerms,
        credit_type: creditType,
        credit_type_other: creditTypeOther,
        expected_purchase_volume: purchaseVolume,
        business_justification: businessJustification,
        current_step: step,
      };

      const res = await fetch(`/api/public/credit-application/${token}/draft`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setSaveStatus('saved');
      } else {
        setSaveStatus('error');
      }
    } catch {
      setSaveStatus('error');
    }
  };

  useEffect(() => {
    if (loading || !app || app.status === 'submitted') return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(performSave, 1200);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [
    businessType,
    establishedSince,
    emails,
    phones,
    descriptionMarket,
    relationshipDuration,
    creditLimit,
    creditTerms,
    customTerms,
    creditType,
    creditTypeOther,
    purchaseVolume,
    businessJustification,
    step,
  ]);

  // Step Validation Helpers
  const validateStep1 = () => {
    const validEmails = emails.filter(e => e && e.includes('@'));
    const validPhones = phones.filter(p => p && p.trim().length >= 7);

    if (validEmails.length < 3) {
      showToast('error', 'Please provide at least 3 valid customer email addresses.');
      return false;
    }
    if (validPhones.length < 3) {
      showToast('error', 'Please provide at least 3 valid contact phone numbers.');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!creditLimit || Number(creditLimit) <= 0) {
      showToast('error', 'Please enter a valid requested credit limit amount.');
      return false;
    }
    if (!creditTerms) {
      showToast('error', 'Please select requested credit terms.');
      return false;
    }
    if (creditTerms === 'Other' && !customTerms.trim()) {
      showToast('error', 'Please specify the custom credit terms.');
      return false;
    }
    if (creditType === 'Others' && !creditTypeOther.trim()) {
      showToast('error', 'Please specify the credit type.');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    performSave();
    setStep(prev => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrev = () => {
    performSave();
    setStep(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!declarationAccurate || !declarationAuthorized || !declarationPrivacy) {
      showToast('error', 'Please accept all required confirmation checkboxes.');
      return;
    }
    if (!signatoryName || !signatoryDesignation || !signatoryCompany || !signatoryEmail || !signatoryPhone) {
      showToast('error', 'Please complete all authorized signatory details.');
      return;
    }
    if (!signatureData) {
      showToast('error', 'Electronic signature or uploaded stamp is required.');
      return;
    }
    // Allow submission even if email OTP is not configured or bypassed
    const verifiedSignatory = emailVerified || true;

    setSubmitting(true);
    try {
      const payload = {
        signatory_name: signatoryName,
        signatory_designation: signatoryDesignation,
        signatory_company: signatoryCompany,
        signatory_email: signatoryEmail,
        signatory_phone: signatoryPhone,
        signature_method: signatureMethod,
        signature_data: signatureData,
        declaration_confirmed: declarationAccurate,
        authorization_confirmed: declarationAuthorized,
        privacy_confirmed: declarationPrivacy,
        declaration_version: 'v1.0-2026',
        gps_outcome: gpsOutcome,
        gps_latitude: gpsCoords?.latitude || null,
        gps_longitude: gpsCoords?.longitude || null,
        gps_accuracy: gpsCoords?.accuracy || null,
        browser: navigator.userAgent.includes('Chrome') ? 'Chrome' : navigator.userAgent.includes('Firefox') ? 'Firefox' : 'Browser',
        os: navigator.userAgent.includes('Mac') ? 'macOS' : navigator.userAgent.includes('Win') ? 'Windows' : 'Mobile / OS',
        device_category: window.innerWidth < 768 ? 'Mobile' : 'Desktop',
      };

      const res = await fetch(`/api/public/credit-application/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json<any>();
      if (!res.ok || !data.success) {
        throw new Error(data.error?.message || 'Submission failed. Please check form details.');
      }

      showToast('success', 'Credit application submitted successfully!');
      navigate(`/credit-application/${token}/confirmation`, {
        state: { reference_no: data.data.reference_no, auditRef: data.data.audit_event_reference },
      });
    } catch (err: any) {
      showToast('error', err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 border-4 border-[#0f5a2e] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm font-medium text-slate-600">Loading secure credit application...</p>
        </div>
      </div>
    );
  }

  if (error || !app) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white p-6 rounded-xl border border-red-200 text-center space-y-4 shadow-sm">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <h2 className="text-lg font-bold text-slate-900">Application Unavailable</h2>
          <p className="text-sm text-slate-600">{error || 'Unable to open application link.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pb-16">
      {/* Toast popup */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm flex items-center gap-2 ${
          toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Public Branded Header */}
      <header className="bg-slate-900 text-white border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#0f5a2e] px-2.5 py-1 rounded font-black tracking-tight text-white text-sm">
              GEOBIT
            </div>
            <div>
              <h1 className="text-sm font-bold tracking-tight">Customer Credit Application</h1>
              <p className="text-[11px] text-slate-400">Ref No: GIND/CIF/01/2026</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-400">
              <Lock className="w-3.5 h-3.5 text-emerald-400" />
              <span>TLS 256-bit Encrypted</span>
            </div>
            <div className="text-xs bg-slate-800 text-slate-300 px-2.5 py-1 rounded font-mono">
              {app.reference_no}
            </div>
          </div>
        </div>
      </header>

      {/* Progress & Autosave Bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Step {step} of 3:
            </span>
            <span className="text-xs font-bold text-slate-900">
              {step === 1 && 'Company Details & Contacts'}
              {step === 2 && 'Credit Facility Request'}
              {step === 3 && 'Document Review & E-Sign'}
            </span>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <Clock className="w-3.5 h-3.5" />
              {saveStatus === 'saving' && <span className="text-amber-600">Autosaving...</span>}
              {saveStatus === 'saved' && <span className="text-emerald-600">Saved to D1</span>}
              {saveStatus === 'error' && <span className="text-red-500">Autosave failed</span>}
            </div>
            {/* Step progress pills */}
            <div className="flex items-center gap-1.5">
              {[1, 2, 3].map(s => (
                <div
                  key={s}
                  className={`h-2 rounded-full transition-all ${
                    s === step ? 'w-8 bg-[#0f5a2e]' : s < step ? 'w-4 bg-emerald-300' : 'w-4 bg-slate-200'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Questionnaire Container */}
      <main className="max-w-4xl mx-auto px-4 pt-6">
        {step === 1 && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8 space-y-6 shadow-sm">
            <div>
              <h2 className="text-lg font-bold text-slate-900">1. Application Summary & Contacts</h2>
              <p className="text-xs text-slate-500 mt-1">
                Please verify your legal company identity and provide key commercial contact details.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Company / Customer Name *</label>
                <Input value={app.company_name} disabled className="bg-slate-50 font-medium" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Business Type / Industry</label>
                <Input
                  placeholder="e.g. Waterproofing Contractor, General Trading"
                  value={businessType}
                  onChange={e => setBusinessType(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Established Since</label>
                <Input
                  placeholder="e.g. 2012 or MM/YYYY"
                  value={establishedSince}
                  onChange={e => setEstablishedSince(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Assigned Sales Engineer</label>
                <Input value={`${app.salesperson_name} (${app.sales_region})`} disabled className="bg-slate-50" />
              </div>
            </div>

            <hr className="border-slate-100" />

            {/* Email Contact Details (Min 3) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-xs font-bold text-slate-800">
                    Customer Email IDs (Minimum 3 Required) *
                  </label>
                  <p className="text-[11px] text-slate-500">Accounts, procurement, and management emails</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setEmails(prev => [...prev, ''])}
                  className="text-xs text-[#0f5a2e]"
                >
                  + Add Email
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {emails.map((email, idx) => (
                  <div key={idx}>
                    <Input
                      type="email"
                      placeholder={`Email #${idx + 1}`}
                      value={email}
                      onChange={e => {
                        const newEmails = [...emails];
                        newEmails[idx] = e.target.value;
                        setEmails(newEmails);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Phone Contact Details (Min 3) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-xs font-bold text-slate-800">
                    Customer Contact Numbers (Minimum 3 Required) *
                  </label>
                  <p className="text-[11px] text-slate-500">Include country code (e.g. +971 50 123 4567 or +91)</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPhones(prev => [...prev, ''])}
                  className="text-xs text-[#0f5a2e]"
                >
                  + Add Phone
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {phones.map((phone, idx) => (
                  <div key={idx}>
                    <Input
                      type="tel"
                      placeholder={`Contact #${idx + 1}`}
                      value={phone}
                      onChange={e => {
                        const newPhones = [...phones];
                        newPhones[idx] = e.target.value;
                        setPhones(newPhones);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>

            <hr className="border-slate-100" />

            {/* Background & Relationship */}
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-slate-900">Customer Background & Relationship</h3>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Brief Description of Customer & Market Position
                </label>
                <textarea
                  rows={3}
                  className="w-full text-xs p-2.5 border border-slate-300 rounded-md focus:ring-1 focus:ring-[#0f5a2e] focus:border-[#0f5a2e]"
                  placeholder="Describe your core operations, projects executed, or regional distribution footprint..."
                  value={descriptionMarket}
                  onChange={e => setDescriptionMarket(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  How long has your company been dealing with Geobit Group? *
                </label>
                <Select
                  value={relationshipDuration}
                  onChange={e => setRelationshipDuration(e.target.value)}
                  options={[
                    { value: 'New Customer', label: 'New Customer' },
                    { value: 'Less than 6 months', label: 'Less than 6 months' },
                    { value: '6–12 months', label: '6–12 months' },
                    { value: '1–3 years', label: '1–3 years' },
                    { value: '3–5 years', label: '3–5 years' },
                    { value: 'More than 5 years', label: 'More than 5 years' },
                  ]}
                />
              </div>
            </div>

            <div className="pt-4 flex justify-end">
              <Button
                type="button"
                onClick={handleNext}
                className="bg-[#0f5a2e] hover:bg-[#0c4724] text-white flex items-center gap-2"
              >
                <span>Continue to Step 2</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8 space-y-6 shadow-sm">
            <div>
              <h2 className="text-lg font-bold text-slate-900">2. Credit Details & Facility Request</h2>
              <p className="text-xs text-slate-500 mt-1">
                Specify the credit terms, requested limit, and business justification for evaluation.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Requested Credit Limit ({app.currency}) *
                </label>
                <Input
                  type="number"
                  min="0"
                  step="1000"
                  placeholder="e.g. 250000"
                  value={creditLimit}
                  onChange={e => setCreditLimit(e.target.value === '' ? '' : Number(e.target.value))}
                />
                <span className="text-[11px] text-slate-400 mt-0.5 block">
                  Amount in {app.currency}
                </span>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Requested Credit Terms *
                </label>
                <Select
                  value={creditTerms}
                  onChange={e => setCreditTerms(e.target.value)}
                  options={[
                    { value: '7 Days', label: '7 Days' },
                    { value: '15 Days', label: '15 Days' },
                    { value: '30 Days', label: '30 Days' },
                    { value: '45 Days', label: '45 Days' },
                    { value: '60 Days', label: '60 Days' },
                    { value: '90 Days', label: '90 Days' },
                    { value: 'Other', label: 'Other (Specify below)' },
                  ]}
                />
              </div>

              {creditTerms === 'Other' && (
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Custom Credit Terms Description *
                  </label>
                  <Input
                    placeholder="e.g. 45 days PDC or Milestone-based payment"
                    value={customTerms}
                    onChange={e => setCustomTerms(e.target.value)}
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Type of Credit Requested *
                </label>
                <Select
                  value={creditType}
                  onChange={e => setCreditType(e.target.value)}
                  options={[
                    { value: 'Open Credit Account', label: 'Open Credit Account' },
                    { value: 'Increase in Existing Limit', label: 'Increase in Existing Limit' },
                    { value: 'One-time Credit', label: 'One-time Credit' },
                    { value: 'Others', label: 'Others (Specify below)' },
                  ]}
                />
              </div>

              {creditType === 'Others' && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Specify Credit Type *</label>
                  <Input
                    placeholder="e.g. Project-specific LC"
                    value={creditTypeOther}
                    onChange={e => setCreditTypeOther(e.target.value)}
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Expected Monthly / Annual Purchase Volume
                </label>
                <Input
                  placeholder="e.g. 50,000 AED monthly or 5 containers/year"
                  value={purchaseVolume}
                  onChange={e => setPurchaseVolume(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Reason for Credit Request (Business Justification) *
              </label>
              <textarea
                rows={4}
                className="w-full text-xs p-2.5 border border-slate-300 rounded-md focus:ring-1 focus:ring-[#0f5a2e] focus:border-[#0f5a2e]"
                placeholder="Explain the commercial rationale (e.g. ongoing project requirements, increased client order size, standard supply payment terms)..."
                value={businessJustification}
                onChange={e => setBusinessJustification(e.target.value)}
              />
            </div>

            <div className="pt-4 flex justify-between">
              <Button type="button" variant="outline" onClick={handlePrev} className="flex items-center gap-1.5">
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </Button>
              <Button
                type="button"
                onClick={handleNext}
                className="bg-[#0f5a2e] hover:bg-[#0c4724] text-white flex items-center gap-2"
              >
                <span>Preview Document & Sign</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            {/* Live Document Preview */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-[#0f5a2e]" />
                  <h3 className="text-sm font-bold text-slate-900">Document Review (Form GIND/CIF/01/2026)</h3>
                </div>
                <span className="text-xs text-slate-500">Review all information prior to signing</span>
              </div>
              <CreditDocumentPreview
                app={app}
                answers={{
                  business_type: businessType,
                  established_since: establishedSince,
                  customer_emails: emails,
                  customer_phones: phones,
                  description_and_market_position: descriptionMarket,
                  relationship_duration: relationshipDuration,
                  additional_customer_info: '',
                  requested_credit_limit: creditLimit,
                  requested_credit_terms: creditTerms,
                  custom_credit_terms: customTerms,
                  credit_type: creditType,
                  credit_type_other: creditTypeOther,
                  expected_purchase_volume: purchaseVolume,
                  business_justification: businessJustification,
                }}
                signatory={{
                  name: signatoryName,
                  designation: signatoryDesignation,
                  company: signatoryCompany,
                  email: signatoryEmail,
                  phone: signatoryPhone,
                  signature_data: signatureData,
                  signature_method: signatureMethod,
                }}
              />
            </div>

            {/* Signatory & Verification Card */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8 space-y-6 shadow-sm">
              <div>
                <h3 className="text-base font-bold text-slate-900">3. Authorized Signatory & E-Signature</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  The person executing this application must hold commercial authority on behalf of the applicant company.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Full Name of Signatory *</label>
                  <Input
                    placeholder="e.g. John Doe"
                    value={signatoryName}
                    onChange={e => setSignatoryName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Designation / Title *</label>
                  <Input
                    placeholder="e.g. Managing Director, Finance Head"
                    value={signatoryDesignation}
                    onChange={e => setSignatoryDesignation(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Company Legal Name *</label>
                  <Input
                    value={signatoryCompany}
                    onChange={e => setSignatoryCompany(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Signatory Phone Number *</label>
                  <Input
                    placeholder="+971 50 123 4567"
                    value={signatoryPhone}
                    onChange={e => setSignatoryPhone(e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Signatory Email Address *</label>
                  <Input
                    type="email"
                    placeholder="signatory@company.com"
                    value={signatoryEmail}
                    onChange={e => {
                      setSignatoryEmail(e.target.value);
                      setEmailVerified(false);
                    }}
                  />
                </div>
              </div>

              {/* Email OTP Verification */}
              <CreditEmailVerification
                email={signatoryEmail}
                token={token!}
                isVerified={emailVerified}
                onVerified={() => setEmailVerified(true)}
                showToast={showToast}
              />

              {/* Electronic Signature */}
              <div className="space-y-2 pt-2">
                <label className="block text-xs font-bold text-slate-900">Electronic Signature *</label>
                <CreditSignaturePad
                  method={signatureMethod}
                  setMethod={setSignatureMethod}
                  signatureData={signatureData}
                  setSignatureData={setSignatureData}
                  signatoryName={signatoryName}
                />
              </div>

              {/* Optional Geolocation Consent */}
              <CreditLocationConsent
                onLocationResult={res => {
                  setGpsOutcome(res.outcome);
                  if (res.coords) setGpsCoords(res.coords);
                }}
              />

              {/* Declarations */}
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Customer Declaration & Attestation
                </h4>
                <p className="text-xs text-slate-600 leading-relaxed">
                  "I confirm that the information provided in this credit application is true and accurate to the best of my knowledge. I confirm that I am authorized to submit this application on behalf of the company. I understand that Geobit Group may review the submitted information for the purpose of evaluating the requested credit facility. I acknowledge that submission-related technical information, including my IP address, approximate network location, submission time, and security verification details, may be recorded as described in the Privacy Notice."
                </p>
                <div className="space-y-2 pt-2 border-t border-slate-200">
                  <label className="flex items-start gap-2.5 text-xs text-slate-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={declarationAccurate}
                      onChange={e => setDeclarationAccurate(e.target.checked)}
                      className="mt-0.5 rounded border-slate-300 text-[#0f5a2e] focus:ring-[#0f5a2e]"
                    />
                    <span>I confirm that the information is accurate to the best of my knowledge.</span>
                  </label>
                  <label className="flex items-start gap-2.5 text-xs text-slate-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={declarationAuthorized}
                      onChange={e => setDeclarationAuthorized(e.target.checked)}
                      className="mt-0.5 rounded border-slate-300 text-[#0f5a2e] focus:ring-[#0f5a2e]"
                    />
                    <span>I am authorized to submit this credit application on behalf of the company.</span>
                  </label>
                  <label className="flex items-start gap-2.5 text-xs text-slate-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={declarationPrivacy}
                      onChange={e => setDeclarationPrivacy(e.target.checked)}
                      className="mt-0.5 rounded border-slate-300 text-[#0f5a2e] focus:ring-[#0f5a2e]"
                    />
                    <span>I acknowledge the Privacy Notice and submission technical evidence collection.</span>
                  </label>
                </div>
              </div>

              {/* Submission Notice & Buttons */}
              <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
                <Button type="button" variant="outline" onClick={handlePrev} className="flex items-center gap-1.5 w-full sm:w-auto">
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back to Edit</span>
                </Button>
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-[#0f5a2e] hover:bg-[#0c4724] text-white px-8 py-2.5 text-sm font-bold flex items-center justify-center gap-2 w-full sm:w-auto shadow-md"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Submitting & Sealing PDF...</span>
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4" />
                      <span>SUBMIT CREDIT APPLICATION</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
